import { contentSlug, CONTENT_SLUG_PATTERN } from '../../../../content-slug';
import { Suburb, SuburbBlock } from '../../../../models/content';

/**
 * A suburb's Page tab (#262), as values: its page settings and the content fields the template's blocks read. An
 * empty field hides its block, so a cleared field is removed from the record rather than stored empty. Pure.
 */
export interface PageTabValues {
  /** Records not from the map (sub-regions, places) name themselves and pick a region. */
  name: string;
  regionId: string;
  slug: string;
  /** {{suburb:where}}: "the Eastern Suburbs". Blank = the name. */
  inSentence: string;
  /** The meta description. Blank = the opening paragraph, else the site's own. */
  seoDescription: string;
  noindex: boolean;
  /** The opening paragraph: the hero's lead (plain text). */
  intro: string;
  /** About: the t-about block's text (Markdown). */
  body: string;
  heroFileId: number | null;
  faqIds: string[];
}

type Field = keyof PageTabValues;
const TEXT_FIELDS: Field[] = ['inSentence', 'seoDescription', 'intro', 'body'];

export function pageTabValues(record: Partial<Suburb>): PageTabValues {
  return {
    name: record.name ?? '',
    regionId: record.regionId ?? '',
    slug: record.slug || contentSlug(record.name ?? ''),
    inSentence: record.inSentence ?? '',
    seoDescription: record.seoDescription ?? '',
    noindex: record.noindex === true,
    intro: record.intro ?? '',
    body: record.body ?? '',
    heroFileId: typeof record.heroFileId === 'number' ? record.heroFileId : null,
    faqIds: (record.faqIds ?? []).filter((id) => typeof id === 'string' && !!id),
  };
}

/** Why the tab can't be saved yet, or null. */
export function pageTabProblem(v: PageTabValues, fromMap: boolean): string | null {
  if (!fromMap && !v.name.trim()) return 'Give the page a name.';
  if (!fromMap && !v.regionId) return 'Pick the region this page belongs to.';
  if (!CONTENT_SLUG_PATTERN.test(contentSlug(v.slug)))
    return 'The address needs letters or numbers.';
  return null;
}

export interface PageTabSave {
  /** The record as it was when the tab opened. */
  original: Suburb;
  values: PageTabValues;
  /** The inserted blocks as the record stores them (blocksForSave). */
  blocks: Record<string, SuburbBlock>;
  /** A suburb from the map: its name and region come from there. */
  fromMap: boolean;
  /** On the website when the tab opened: a rename keeps the old slug so the site redirects it. */
  live: boolean;
  /** Publish (true), Un-publish (false), or Save (null: as it is). */
  published: boolean | null;
}

/**
 * The tab's changes applied to a freshly read copy of the record. Only what the person changed is written: a field
 * left alone keeps the fresh copy's value, so an AI draft accepted (or an owner set) while the tab was open isn't
 * undone by a save from the older copy.
 */
export function applyPageTab(fresh: Suburb, s: PageTabSave): Suburb {
  const before = pageTabValues(s.original);
  const changed = (f: Field) => !sameValue(before[f], s.values[f]);
  const next: Suburb = { ...fresh };
  const set = (key: keyof Suburb, value: unknown, empty: boolean) => {
    if (empty) delete next[key];
    else (next as unknown as Record<string, unknown>)[key] = value;
  };

  if (!s.fromMap && changed('name')) next.name = s.values.name.trim();
  if (!s.fromMap && changed('regionId')) next.regionId = s.values.regionId;
  if (changed('slug')) renameTo(next, contentSlug(s.values.slug), s.live);
  for (const f of TEXT_FIELDS) {
    if (!changed(f)) continue;
    const text = String(s.values[f] ?? '').trim();
    set(f as keyof Suburb, text, !text);
  }
  if (changed('heroFileId')) set('heroFileId', s.values.heroFileId, s.values.heroFileId === null);
  if (changed('faqIds')) set('faqIds', [...s.values.faqIds], !s.values.faqIds.length);
  if (changed('noindex')) set('noindex', true, !s.values.noindex);
  if (!sameValue(s.blocks, blocksOf(s.original)))
    set('blocks', s.blocks, !Object.keys(s.blocks).length);
  if (s.published !== null) next.published = s.published;
  return next;
}

/** Whether the tab holds changes not saved yet: a field, or the inserted blocks (as the save would write them). */
export function hasUnsavedChanges(s: Pick<PageTabSave, 'original' | 'values' | 'blocks'>): boolean {
  const before = pageTabValues(s.original);
  const fields = Object.keys(before) as Field[];
  return (
    fields.some((f) => !sameValue(before[f], s.values[f])) ||
    !sameValue(s.blocks, blocksOf(s.original))
  );
}

/** A live page renamed keeps its old slug, so the site 301s it; moving back to an earlier slug drops it from the list. */
function renameTo(next: Suburb, slug: string, live: boolean): void {
  const previous = new Set((next.redirectFrom ?? []).filter((x) => typeof x === 'string'));
  if (live && next.slug && next.slug !== slug) previous.add(next.slug);
  previous.delete(slug);
  next.slug = slug;
  if (previous.size) next.redirectFrom = [...previous];
  else delete next.redirectFrom;
}

function blocksOf(record: Partial<Suburb>): Record<string, SuburbBlock> {
  return record.blocks && typeof record.blocks === 'object' ? record.blocks : {};
}

/** Value equality that ignores key order (blocks are rebuilt by the editor). */
export function sameValue(a: unknown, b: unknown): boolean {
  return stable(a) === stable(b);
}

function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/** Words in a paragraph, for the opening paragraph's count. */
export function wordCount(text: string | null | undefined): number {
  return (text ?? '').trim().split(/\s+/).filter(Boolean).length;
}
